import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, ArrowRight, Sparkles, Tag, ExternalLink, Heart, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Instagram Icon Component
const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

export default function News() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    axios.get(`${API}/news`)
      .then(res => {
        setNews(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const categories = [
    { id: 'all', label: t('news.allCategories') },
    { id: 'member', label: '🎉 Nieuwe Leden' },
    { id: 'purchase', label: '💎 Aankopen' },
    { id: 'instagram', label: '📸 Instagram' },
    { id: 'news', label: t('news.categoryNews') },
    { id: 'update', label: t('news.categoryUpdate') },
    { id: 'announcement', label: t('news.categoryAnnouncement') },
  ];

  const filteredNews = selectedCategory === 'all' 
    ? news 
    : news.filter(article => article.category === selectedCategory);

  const getTitle = (article) => {
    if (i18n.language === 'nl' && article.title_nl) return article.title_nl;
    return article.title;
  };

  const getContent = (article) => {
    if (i18n.language === 'nl' && article.content_nl) return article.content_nl;
    return article.content;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'announcement': return 'bg-[#D4AF37]/20 text-[#D4AF37]';
      case 'update': return 'bg-blue-500/20 text-blue-400';
      case 'instagram': return 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-pink-400';
      default: return 'bg-white/10 text-gray-300';
    }
  };

  const isInstagramPost = (article) => article.category === 'instagram' || article.instagram_url;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">{t('news.badge')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight mb-4">
            {t('news.title')} <span className="brand-gold text-5xl sm:text-6xl">{t('news.titleAccent')}</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            {t('news.subtitle')}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                selectedCategory === category.id
                  ? 'bg-[#D4AF37] text-black font-medium'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`category-${category.id}`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* News Grid */}
        {filteredNews.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">{t('news.noArticles')}</p>
            <p className="text-gray-500 mt-2">{t('news.checkBackLater')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((article, index) => (
              <article 
                key={article.id}
                className={`group glass rounded-2xl overflow-hidden border transition-all duration-500 animate-fade-in ${
                  isInstagramPost(article)
                    ? 'border-purple-500/20 hover:border-pink-500/50'
                    : 'border-white/5 hover:border-[#D4AF37]/30'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`news-article-${index}`}
              >
                {/* Image */}
                {article.image_url && (
                  <div className="aspect-square overflow-hidden relative">
                    <img 
                      src={article.image_url}
                      alt={getTitle(article)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {/* Instagram overlay */}
                    {isInstagramPost(article) && (
                      <div className="absolute top-3 right-3 p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                        <InstagramIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-mono flex items-center gap-1 ${getCategoryColor(article.category)}`}>
                      {isInstagramPost(article) ? (
                        <InstagramIcon className="w-3 h-3" />
                      ) : (
                        <Tag className="w-3 h-3" />
                      )}
                      {article.category}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(article.created_at)}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className={`text-xl font-medium mb-3 transition-colors ${
                    isInstagramPost(article) 
                      ? 'group-hover:text-pink-400'
                      : 'group-hover:text-[#D4AF37]'
                  }`}>
                    {getTitle(article)}
                  </h2>

                  {/* Preview */}
                  <p className="text-gray-400 text-sm line-clamp-3 mb-4">
                    {getContent(article)}
                  </p>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    {isInstagramPost(article) && article.instagram_url ? (
                      <a 
                        href={article.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full text-pink-400 text-sm font-medium hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
                        data-testid={`instagram-link-${index}`}
                      >
                        <InstagramIcon className="w-4 h-4" />
                        Bekijk op Instagram
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <button 
                        onClick={() => navigate(`/news/${article.id}`)}
                        className="text-[#D4AF37] text-sm font-medium flex items-center gap-2 hover:gap-3 transition-all"
                        data-testid={`read-more-${index}`}
                      >
                        {t('news.readMore')}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}

                    {/* Engagement indicators for Instagram */}
                    {isInstagramPost(article) && (
                      <div className="flex items-center gap-3 text-gray-500 text-sm">
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-20 text-center">
          <div className="inline-block p-8 glass rounded-2xl">
            <h3 className="text-2xl font-medium mb-4">
              {t('news.ctaTitle')} <span className="gold-text">{t('news.ctaTitleAccent')}</span>
            </h3>
            <p className="text-gray-400 mb-6 max-w-md">
              {t('news.ctaSubtitle')}
            </p>
            <Button 
              onClick={() => navigate('/packages')}
              className="btn-gold px-8 py-6 text-lg"
              data-testid="news-cta-btn"
            >
              {t('news.ctaButton')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
